import { map } from 'rxjs/operators';
import { Injectable } from '@angular/core'
import { AbstractControl, AsyncValidatorFn, FormArray, FormControl, FormGroup, ValidatorFn } from '@angular/forms';
import { AbstractControlOptions } from '@angular/forms/src/model'

import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';

import { ControlConfig, FieldMap, GroupConfig, ValidationResult, ValidationTranslation, VALIDATORS } from '../validators';
import { hasProps } from '../helpers/object';

const validatorTranslations = VALIDATORS.reduce(
    (result, current) => ({
        ...result,
        [current]: 'validation.' + current.toLowerCase()
    }), {})

export class TranslatedFormGroup extends FormGroup {
    fieldMap: FieldMap

    constructor(
        fieldMap$: Observable<FieldMap>,
        controls: {[key: string]: AbstractControl},
        validatorOrOpts?: ValidatorFn | ValidatorFn[] | AbstractControlOptions | null,
        asyncValidator?: AsyncValidatorFn | AsyncValidatorFn[] | null
    ) {
        super(controls, validatorOrOpts, asyncValidator)

        fieldMap$.subscribe(map => this.fieldMap = map)
    }

    /**
     * Runs validation on the form group and returns all errors.
     */
    validate(): ValidationResult {
        let errors = ValidationTranslation.humanizeErrors(
            this, validatorTranslations, this.fieldMap
        )
        
        return {
            hasErrors: hasProps(errors), errors
        }
    }
}

@Injectable()
export class FormsService {
    constructor(
        private translations: TranslateService
    ) {}

    /**
     * Creates a translated form group
     * @param controlConfig The formgroup configuration object.
     */
    group(config: GroupConfig): TranslatedFormGroup {
        return new TranslatedFormGroup(
            this.translateFieldMap(config), 
            this.createControls(config))
    }

    private createControls(controls: GroupConfig): {[key: string]: AbstractControl} {
        return Object.keys(controls).reduce(
            (controlDict, current) => ({
                ...controlDict,
                [current]: this.createSingle(controls[current])
            }), {})
    }

    private createSingle(config: ControlConfig): AbstractControl {
        if(this.isControlInstance(config)) {
            return <AbstractControl>config
        } else if(Array.isArray(config)) {
            let value = config[0]
            let validators = config.length > 1
                ? config[1] : null

            return new FormControl(value, validators)
        } else {
            return new FormControl(config)
        }
    }

    private isControlInstance(config: any) {
        return config instanceof FormControl
            || config instanceof FormGroup
            || config instanceof FormArray
    }

    private translateFieldMap(config: GroupConfig): Observable<FieldMap> {
        let fields = Object.keys(config)
        let translationCodes = []
        let reverseLookup = {}

        fields.forEach(key => {
            let current = config[key]
            if(Array.isArray(current) && current.length > 2) {
                translationCodes.push(current[2])
                reverseLookup[current[2]] = key
            }
        })
        
        return this.translations
            .stream(translationCodes).pipe(
            map(translations => Object.keys(translations).reduce(
                (result, key, i) =>  ({
                    ...result,
                    [reverseLookup[key]]: translations[key]
                }), {})))
    }
}
